/**
 * The inbound blocklist, as a screen.
 *
 * A number published in the business register is called by sales robots within
 * weeks, and every one of those calls used to cost what a customer costs: the
 * announcement plays, every operator's browser rings, a Twilio minute is billed
 * and a row lands in a list somebody has to read. The only answer the module had
 * was switching the whole inbound line off.
 *
 * The screen exists because the list is otherwise invisible in both directions.
 * A blocked caller is refused before the call is answered, so nothing rings and
 * no row appears in any folder anybody works: there is no way to see that the
 * list is doing its job, and no way to see that it has caught the wrong number.
 * That second one is the reason for the warning strip and the badge - a blocked
 * client simply stops being able to reach us, and nothing else in the CRM would
 * ever say so.
 *
 * It lives inside the call desk beside the do-not-call screen, and it is
 * deliberately NOT part of it. The two lists are opposites: that one records
 * somebody asking US to stop, this one records US refusing THEM.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Ban, Loader2, Plus, Search, SlidersHorizontal } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  listInboundBlocks,
  removeInboundBlock,
  type InboundBlock,
} from "../../../../lib/callBlocklist";
import { formatPhone } from "../../../../lib/phone";
import { Btn, EmptyState, IconBtn, fmtNum, listCard } from "../../../ui";
import { Pop, useClosePops } from "../../../shell/Pop";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { RailTrigger } from "../../_shared/workMobile";
import { AddBlockModal } from "./AddBlockModal";
import { BlocklistRow } from "./BlocklistRow";
import {
  ANY_SOURCE,
  DEFAULT_BLOCKLIST_FILTER,
  INBOUND_BLOCK_SOURCES,
  blocklistFilterActive,
  filterBlocklist,
  knownPartyCount,
  type BlocklistFilter,
} from "./rows";

/** Who, why, the note, the calls behind it, when it was blocked, and the menu.
 *  Shared by the head and every row so a column cannot move in one without
 *  moving in the other. */
const COLS = "minmax(0,2.1fr) 1fr minmax(0,1.5fr) 0.9fr 1.1fr 44px";

export function BlocklistPanel({
  canManage,
  onRail,
}: {
  /**
   * ONE right for both directions, which is where this differs from the
   * do-not-call screen next door. There, adding is safe and removing is what
   * can do harm, so the two were split. Here the harm is the entry itself: a
   * blocked customer is unreachable with no error either side ever sees, and
   * the only cure is taking the entry off again. Locking the cure behind a
   * wider key would leave the mistake standing until somebody else logged in.
   *
   * Both routes behind this are on `use:crm.calls` for the same reason - the
   * only human who can tell a robot from a customer is the one who just
   * answered it.
   */
  canManage: boolean;
  onRail: () => void;
}) {
  const t = useT();
  const [rows, setRows] = useState<InboundBlock[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<BlocklistFilter>(DEFAULT_BLOCKLIST_FILTER);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDel, setConfirmDel] = useState<InboundBlock | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      // `context` is what turns a list of numbers into a list of companies, and
      // on this screen it is not a nicety: it is the only thing that can say a
      // blocked number belongs to a client.
      const res = await listInboundBlocks({ context: true });
      setRows(res.rows);
      setError(null);
    } catch {
      setRows([]);
      setError(t("calls.blocklist.loadError"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => filterBlocklist(rows ?? [], filter), [rows, filter]);
  const narrowed = blocklistFilterActive(filter);
  const known = useMemo(() => knownPartyCount(rows ?? []), [rows]);

  async function remove(row: InboundBlock) {
    try {
      await removeInboundBlock(row.id);
      setRows((cur) => (cur ?? []).filter((r) => r.id !== row.id));
      setError(null);
    } catch {
      setError(t("calls.blocklist.removeFailed"));
    } finally {
      setConfirmDel(null);
    }
  }

  return (
    <div className="crm-card crm-work-col crm-work-list" style={listCard(COLS)}>
      {/* The same number strip the call log wears. The middle figure is the one
          worth having: how many of these entries were judged by a person who
          heard the caller, as against typed in from somewhere else. */}
      <div className="crm-cstats">
        <div className="crm-cstat">
          <b>{fmtNum(rows?.length ?? 0)}</b>
          <span>{t("calls.blocklist.statTotal")}</span>
        </div>
        <div className="crm-cstat">
          <b>{fmtNum((rows ?? []).filter((r) => r.source === "spam").length)}</b>
          <span>{t("calls.blocklist.statSpam")}</span>
        </div>
        <div className="crm-cstat">
          <b>{fmtNum(known)}</b>
          <span>{t("calls.blocklist.statKnown")}</span>
        </div>
      </div>

      <div className="crm-pane-head pt-3.5">
        <RailTrigger label={t("calls.pane.views")} onOpen={onRail} />
        <label className="crm-pane-search">
          <Search className="size-4" />
          <input
            type="search"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            placeholder={t("calls.blocklist.searchPlaceholder")}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        {rows === null ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}

        <SourceFilter filter={filter} onFilter={setFilter} />

        {canManage ? (
          <Btn small tone="brand" onClick={() => setShowAdd(true)}>
            <Plus className="size-4" />
            {t("calls.blocklist.add")}
          </Btn>
        ) : null}
      </div>

      {error ? (
        <div className="mx-4 mb-2 flex items-center gap-2 text-[12.5px] text-destructive">
          <AlertTriangle className="size-3.5" />
          {error}
        </div>
      ) : null}

      {/* Above the list rather than only on the rows, and only when there is
          something to say. This is the one thing on the screen that asks for
          action: an entry turning away a client or a lead is a mistake that
          reports itself nowhere else, and somebody scrolling a long list would
          never reach the badge. */}
      {known > 0 ? (
        <div className="mx-4 mb-2 flex items-start gap-2 text-[12.5px] text-[var(--color-warning,var(--color-destructive))]">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{t("calls.blocklist.knownWarning", { n: known })}</span>
        </div>
      ) : null}

      <div className="crm-pane-scroll">
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : shown.length === 0 ? (
          // An empty list and an empty search are different facts. Saying
          // "nobody is blocked" over an active filter sends somebody looking
          // for a bug in the blocking, which works.
          <EmptyState
            icon={Ban}
            title={t(narrowed ? "calls.blocklist.noMatch" : "calls.blocklist.empty")}
            body={t(narrowed ? "calls.blocklist.noMatchBody" : "calls.blocklist.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 6 }}>
              <span>{t("calls.blocklist.col.who")}</span>
              <span>{t("calls.blocklist.col.source")}</span>
              <span>{t("calls.blocklist.col.note")}</span>
              <span>{t("calls.blocklist.col.calls")}</span>
              <span>{t("calls.blocklist.col.added")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {shown.map((row) => (
                <BlocklistRow
                  key={row.id}
                  row={row}
                  cols={COLS}
                  canRemove={canManage}
                  onRemove={() => setConfirmDel(row)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {showAdd ? (
        <AddBlockModal
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            // Re-read rather than splicing the answer in: the POST upserts, so
            // blocking a number that is already blocked answers with the row
            // that was already there and splicing would draw it twice.
            void load();
          }}
          onError={() => {
            setShowAdd(false);
            setError(t("calls.blocklist.addError"));
          }}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("calls.blocklist.removeTitle")}
          body={t("calls.blocklist.confirmRemove", { phone: formatPhone(confirmDel.value) })}
          confirmLabel={t("calls.blocklist.remove")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </div>
  );
}

/** Why the number is on the list, as the head's one filter. A menu rather than
 *  a row of pills, matching the do-not-call screen: the head already carries a
 *  search box and an action. */
function SourceFilter({
  filter,
  onFilter,
}: {
  filter: BlocklistFilter;
  onFilter: (next: BlocklistFilter) => void;
}) {
  const t = useT();
  const close = useClosePops();
  const active = filter.source !== ANY_SOURCE;

  return (
    <Pop
      trigger={(open) => (
        <IconBtn
          className={cn(active && "crm-cb-dot", open && "is-on")}
          title={t("calls.blocklist.filterTitle")}
          aria-label={t("calls.blocklist.filterTitle")}
          aria-expanded={open}
        >
          <SlidersHorizontal className="size-4" />
        </IconBtn>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("calls.blocklist.filterTitle")}</div>
        <div className="crm-menu-s">{t("calls.blocklist.filterSub")}</div>
      </div>

      <button
        type="button"
        className={cn("crm-mi", filter.source === ANY_SOURCE && "is-on")}
        onClick={() => {
          onFilter({ ...filter, source: ANY_SOURCE });
          close();
        }}
      >
        {t("calls.blocklist.sourceAny")}
      </button>
      {INBOUND_BLOCK_SOURCES.map((source) => (
        <button
          key={source}
          type="button"
          className={cn("crm-mi", filter.source === source && "is-on")}
          onClick={() => {
            onFilter({ ...filter, source });
            close();
          }}
        >
          {t(`calls.blocklist.source.${source}`)}
        </button>
      ))}
    </Pop>
  );
}
